import { NotificationVariant } from "../components/dashboard/NotificationsProvider";

export interface GlobalNotification {
  message: string | React.ReactNode;
  variant: (typeof NotificationVariant)[keyof typeof NotificationVariant];
}

/**
 * Global notification service that can be used outside React components
 */
class NotificationService {
  private listeners: Array<(notification: GlobalNotification) => void> = [];

  /**
   * Subscribe to notifications
   * @param listener - Function to call when a notification is triggered
   * @returns Unsubscribe function
   */
  subscribe(listener: (notification: GlobalNotification) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Show a notification
   * @param notification - The notification to display
   */
  notify(notification: GlobalNotification): void {
    this.listeners.forEach((listener) => listener(notification));
  }

  /**
   * Show an error notification
   * @param message - The error message to display
   */
  error(message: string): void {
    this.notify({
      message,
      variant: NotificationVariant.error,
    });
  }

  /**
   * Show a success notification
   * @param message - The success message to display
   */
  success(message: string): void {
    this.notify({
      message,
      variant: NotificationVariant.success,
    });
  }

  /**
   * Show an info notification
   * @param message - The info message to display
   */
  info(message: string): void {
    this.notify({
      message,
      variant: NotificationVariant.info,
    });
  }

  /**
   * Show a warning notification
   * @param message - The warning message to display
   */
  warning(message: string): void {
    this.notify({
      message,
      variant: NotificationVariant.warning,
    });
  }

  /**
   * Show a logout notification specifically for session expiry
   * This creates a persistent notification that survives React unmounting
   */
  showLogoutNotification(): void {
    // First try React notification for immediate feedback
    this.warning("You have been logged out due to inactivity");

    // Then create a persistent DOM-based notification that survives logout
    this.createPersistentLogoutNotification();
  }

  /**
   * Creates a URL with notification parameters for redirects
   * @param baseUrl - The base URL to redirect to
   * @param message - The notification message to show after redirect
   * @param type - The notification type (error, warning, success, info)
   * @returns URL with notification parameters
   */
  createRedirectUrlWithNotification(
    baseUrl: string,
    message: string,
    type: keyof typeof NotificationVariant = "info",
  ): string {
    const url = new URL(baseUrl);
    url.searchParams.set("notification_message", encodeURIComponent(message));
    url.searchParams.set("notification_type", type);
    return url.toString();
  }

  /**
   * Check URL parameters and show logout notification if needed
   * This should be called when the app loads to handle post-redirect notifications
   */
  checkAndShowPostRedirectNotification(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const logoutReason = urlParams.get("logout_reason");
    const notificationMessage = urlParams.get("notification_message");
    const notificationType = urlParams.get("notification_type");

    // Handle specific logout reasons
    if (logoutReason === "inactivity") {
      // Show the persistent notification immediately
      this.createPersistentLogoutNotification();

      // Clean up the URL parameter
      this.removeNotificationParamsFromUrl(["logout_reason"]);
    }

    // Handle generic notification messages
    if (notificationMessage && notificationType) {
      this.createPersistentGenericNotification(
        decodeURIComponent(notificationMessage),
        notificationType as keyof typeof NotificationVariant,
      );

      // Clean up the URL parameters
      this.removeNotificationParamsFromUrl([
        "notification_message",
        "notification_type",
      ]);
    }
  }

  /**
   * Remove notification parameters from URL without triggering a page reload
   */
  private removeNotificationParamsFromUrl(params: string[]): void {
    const url = new URL(window.location.href);

    params.forEach((param) => {
      url.searchParams.delete(param);
    });

    // Update URL without reloading the page
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  /**
   * Creates a generic persistent notification using MUI-inspired styling
   * This creates a notification that looks exactly like MUI Alert/Snackbar
   * but survives React unmounting during redirects
   */
  private createPersistentGenericNotification(
    message: string,
    type: keyof typeof NotificationVariant,
  ): void {
    // Remove any existing persistent notification
    this.removePersistentNotification();

    // Create MUI-styled notification that survives redirects
    this.createMUIStyledPersistentNotification(message, type);
  }

  /**
   * Creates a persistent notification that matches MUI Alert/Snackbar styling exactly
   */
  private createMUIStyledPersistentNotification(
    message: string,
    type: keyof typeof NotificationVariant,
  ): void {
    // Get MUI theme colors
    const muiColors = this.getMUIColors(type);

    // Create main container (Snackbar equivalent)
    const snackbarContainer = document.createElement("div");
    snackbarContainer.id = "persistent-logout-notification";
    snackbarContainer.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1400;
      width: 50%;
      max-width: 500px;
      min-width: 300px;
      animation: slideDown 0.3s ease-out;
    `;

    // Create Alert component (MUI Alert equivalent)
    const alertComponent = document.createElement("div");
    alertComponent.style.cssText = `
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.43;
      letter-spacing: 0.01071em;
      display: flex;
      padding: 6px 16px;
      border-radius: 4px;
      background-color: ${muiColors.backgroundColor};
      color: ${muiColors.textColor};
      box-shadow: 0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12);
      transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    `;

    // Create icon container (MUI Alert icon)
    const iconContainer = document.createElement("div");
    iconContainer.style.cssText = `
      margin-right: 12px;
      padding: 7px 0;
      display: flex;
      font-size: 22px;
      opacity: 0.9;
      color: ${muiColors.iconColor};
    `;
    iconContainer.innerHTML = this.getMUIIcon(type);

    // Create message container
    const messageContainer = document.createElement("div");
    messageContainer.style.cssText = `
      padding: 8px 0;
      min-width: 0;
      overflow: auto;
      flex: 1;
    `;
    messageContainer.textContent = message;

    // Create close button (MUI Alert close button)
    const closeButton = document.createElement("div");
    closeButton.style.cssText = `
      margin-left: auto;
      margin-right: -8px;
      padding: 4px;
      font-size: 18px;
      cursor: pointer;
      opacity: 0.54;
      transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      color: ${muiColors.textColor};
    `;
    closeButton.innerHTML = "✕";
    closeButton.onclick = () => this.removePersistentNotification();

    // Hover effect for close button
    closeButton.onmouseenter = () => {
      closeButton.style.opacity = "1";
      closeButton.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
      closeButton.style.borderRadius = "50%";
    };
    closeButton.onmouseleave = () => {
      closeButton.style.opacity = "0.54";
      closeButton.style.backgroundColor = "transparent";
    };

    // Assemble the notification
    alertComponent.appendChild(iconContainer);
    alertComponent.appendChild(messageContainer);
    alertComponent.appendChild(closeButton);
    snackbarContainer.appendChild(alertComponent);

    // Add animation styles
    this.ensureMUIAnimationStyles();

    // Add to document
    document.body.appendChild(snackbarContainer);

    // Auto-remove after 5 seconds with MUI-style fade out
    setTimeout(() => {
      if (snackbarContainer.parentNode) {
        snackbarContainer.style.animation = "muiFadeOut 0.3s ease-out forwards";
        setTimeout(() => {
          this.removePersistentNotification();
        }, 300);
      }
    }, 5000);
  }

  /**
   * Get MUI-accurate colors for notification types
   */
  private getMUIColors(type: keyof typeof NotificationVariant): {
    backgroundColor: string;
    textColor: string;
    iconColor: string;
  } {
    const isDarkMode = this.detectDarkMode();

    // MUI Alert colors (based on actual MUI theme)
    const muiColorMap = {
      warning: {
        light: {
          bg: "rgb(255, 244, 229)", // warning.light background
          text: "rgb(102, 60, 0)", // warning.dark text
          icon: "rgb(237, 108, 2)", // warning.main icon
        },
        dark: {
          bg: "rgb(51, 30, 0)", // warning background in dark
          text: "rgb(255, 204, 128)", // warning text in dark
          icon: "rgb(255, 152, 0)", // warning icon in dark
        },
      },
      error: {
        light: {
          bg: "rgb(253, 237, 237)", // error.light background
          text: "rgb(95, 33, 32)", // error.dark text
          icon: "rgb(211, 47, 47)", // error.main icon
        },
        dark: {
          bg: "rgb(22, 11, 11)", // error background in dark
          text: "rgb(244, 199, 199)", // error text in dark
          icon: "rgb(244, 67, 54)", // error icon in dark
        },
      },
      success: {
        light: {
          bg: "rgb(237, 247, 237)", // success.light background
          text: "rgb(30, 70, 32)", // success.dark text
          icon: "rgb(46, 125, 50)", // success.main icon
        },
        dark: {
          bg: "rgb(12, 19, 13)", // success background in dark
          text: "rgb(204, 232, 205)", // success text in dark
          icon: "rgb(76, 175, 80)", // success icon in dark
        },
      },
      info: {
        light: {
          bg: "rgb(229, 246, 253)", // info.light background
          text: "rgb(13, 60, 97)", // info.dark text
          icon: "rgb(2, 136, 209)", // info.main icon
        },
        dark: {
          bg: "rgb(7, 19, 24)", // info background in dark
          text: "rgb(184, 231, 251)", // info text in dark
          icon: "rgb(33, 150, 243)", // info icon in dark
        },
      },
    };

    const colors = muiColorMap[type] || muiColorMap.warning;
    const scheme = isDarkMode ? colors.dark : colors.light;

    return {
      backgroundColor: scheme.bg,
      textColor: scheme.text,
      iconColor: scheme.icon,
    };
  }

  /**
   * Get MUI-style icons (using actual MUI Alert icons)
   */
  private getMUIIcon(type: keyof typeof NotificationVariant): string {
    const muiIconMap = {
      warning: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"/>
      </svg>`,
      error: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
      </svg>`,
      success: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>`,
      info: `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
      </svg>`,
    };

    return muiIconMap[type] || muiIconMap.warning;
  }

  /**
   * Get colors for notification type based on current theme (legacy method)
   */
  private getNotificationColors(type: keyof typeof NotificationVariant): {
    backgroundColor: string;
    textColor: string;
    shadowColor: string;
  } {
    const muiColors = this.getMUIColors(type);
    const isDarkMode = this.detectDarkMode();

    return {
      backgroundColor: muiColors.backgroundColor,
      textColor: muiColors.textColor,
      shadowColor: isDarkMode
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.3)",
    };
  }

  /**
   * Get appropriate icon for notification type
   */
  private getNotificationIcon(type: keyof typeof NotificationVariant): string {
    const iconMap = {
      warning: "⚠️",
      error: "❌",
      success: "✅",
      info: "ℹ️",
    };

    return iconMap[type] || iconMap.warning;
  }

  /**
   * Creates a persistent logout notification using direct DOM manipulation
   * This notification will survive React unmounting during logout
   */
  private createPersistentLogoutNotification(): void {
    this.createPersistentGenericNotification(
      "You have been logged out due to inactivity",
      "warning",
    );
  }

  /**
   * Ensures MUI-style animation styles are added to the document
   */
  private ensureMUIAnimationStyles(): void {
    if (!document.getElementById("logout-notification-styles")) {
      const styles = document.createElement("style");
      styles.id = "logout-notification-styles";
      styles.textContent = `
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes muiFadeOut {
          from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          to {
            opacity: 0;
            transform: translateX(-50%) translateY(-8px);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(styles);
    }
  }

  /**
   * Legacy animation styles method
   */
  private ensureAnimationStyles(): void {
    this.ensureMUIAnimationStyles();
  }

  /**
   * Removes any existing persistent notification
   */
  private removePersistentNotification(): void {
    const existingNotification = document.getElementById(
      "persistent-logout-notification",
    );
    if (existingNotification) {
      existingNotification.remove();
    }
  }

  /**
   * Detects if the app is currently in dark mode
   * Checks various indicators like body classes, CSS variables, or system preference
   */
  private detectDarkMode(): boolean {
    // Check if body has dark mode indicators
    const body = document.body;
    const html = document.documentElement;

    // Common dark mode class names
    if (
      body.classList.contains("dark") ||
      body.classList.contains("dark-mode") ||
      html.classList.contains("dark") ||
      html.classList.contains("dark-mode")
    ) {
      return true;
    }

    // Check CSS custom properties for theme indication
    const computedStyle = getComputedStyle(body);
    const bgColor = computedStyle.backgroundColor;

    // If background is dark (simple heuristic)
    if (bgColor) {
      const rgb = bgColor.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        const brightness =
          (parseInt(rgb[0]) * 299 +
            parseInt(rgb[1]) * 587 +
            parseInt(rgb[2]) * 114) /
          1000;
        if (brightness < 128) {
          return true;
        }
      }
    }

    // Fallback to system preference
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
